import { useEffect, useRef, useState } from "react";

/** De 1 a 99; vazio volta para 1 e qualquer outro valor volta para o número salvo. */
export function normalizarPeriodoInicial(texto: string, salvo: number): string {
  const numero = texto.trim() === "" ? 1 : Number(texto);
  return Number.isInteger(numero) && numero >= 1 && numero <= 99 ? String(numero) : String(salvo);
}

interface NumeroDoPeriodoEditavelProps {
  numero: number;
  onSalvar: (numero: number) => void;
}

/**
 * O número no título do primeiro período, editado no próprio lugar. Toda saída
 * da edição passa pelo blur, que é onde se decide entre salvar e cancelar.
 */
export function NumeroDoPeriodoEditavel({ numero, onSalvar }: NumeroDoPeriodoEditavelProps) {
  const numeroExibido = String(numero).padStart(2, "0");
  const [rascunho, setRascunho] = useState<string | null>(null);
  const emEdicao = rascunho !== null;
  const campoRef = useRef<HTMLInputElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const canceladoRef = useRef(false);
  const devolverFocoRef = useRef(false);

  useEffect(() => {
    if (emEdicao) {
      campoRef.current?.focus();
      campoRef.current?.select();
    } else if (devolverFocoRef.current) {
      devolverFocoRef.current = false;
      botaoRef.current?.focus();
    }
  }, [emEdicao]);

  const encerrarEdicao = () => {
    const final = normalizarPeriodoInicial(rascunho ?? "", numero);
    if (!canceladoRef.current && final !== String(numero)) onSalvar(Number(final));
    canceladoRef.current = false;
    setRascunho(null);
  };

  if (!emEdicao) {
    return (
      <button
        ref={botaoRef}
        type="button"
        aria-label={`Editar o número do período inicial, hoje ${numeroExibido}`}
        title="Em uma renovação, comece de outro número"
        className="cursor-text tabular-nums decoration-muted-foreground decoration-dashed underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
        onClick={() => setRascunho(numeroExibido)}
      >
        {numeroExibido}
      </button>
    );
  }

  return (
    <input
      ref={campoRef}
      value={rascunho}
      inputMode="numeric"
      maxLength={2}
      aria-label="Número do período inicial"
      className="w-[2ch] border-b border-dashed border-muted-foreground bg-transparent p-0 tabular-nums outline-none"
      onChange={(event) => setRascunho(event.target.value)}
      onBlur={encerrarEdicao}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== "Escape") return;
        // O foco volta ao botão ainda nesta tecla: sem isso, o Enter o acionaria e reabriria a edição.
        event.preventDefault();
        canceladoRef.current = event.key === "Escape";
        devolverFocoRef.current = true;
        event.currentTarget.blur();
      }}
    />
  );
}
